import CancelIcon from "@mui/icons-material/Cancel";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
  Tooltip,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import type { Delivery } from "@types_def/models/delivery.types";
import { UserRank } from "@types_def/models/user.type";
import { cancellationReasonKeys } from "@utils/constants/deliveryCancelReasons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { useDataTableSelectionBarContext } from "@/components/ui/data-table-selection-bar";
import {
  cancelDeliveriesTableQueries,
  type DeliveriesTableQuerySnapshot,
  removeDeliveriesFromDeliveriesTableCache,
  restoreDeliveriesTableQueries,
  snapshotDeliveriesTableQueries,
} from "@/features/delivery/deliveries-table-cache";
import { useBulkCancelDeliveriesMutation } from "@/libs/generated/hooks";

import {
  invalidateCachesForDeliveryIds,
  toastBulkDeliveryResult,
  toastBulkMutationError,
} from "./bulk-mutation-feedback";
import { useBulkSelectionActionVisibility } from "./use-bulk-selection-action-visibility";

type BulkCancelTableContext = {
  deliveriesTableSnapshot: DeliveriesTableQuerySnapshot;
};

export function BulkCancelAction() {
  const visible = useBulkSelectionActionVisibility({
    requireAdmin: false,
    minRank: UserRank.MAINTAINER,
  });
  const { selectedData, table } = useDataTableSelectionBarContext<Delivery>();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState<(typeof cancellationReasonKeys)[number] | "">("");
  const [additionalInfo, setAdditionalInfo] = useState("");

  const { mutateAsync, isPending } = useBulkCancelDeliveriesMutation<
    unknown,
    BulkCancelTableContext
  >({
    onMutate: async (variables) => {
      await cancelDeliveriesTableQueries(queryClient);
      const deliveriesTableSnapshot = snapshotDeliveriesTableQueries(queryClient);
      removeDeliveriesFromDeliveriesTableCache(queryClient, variables.input.ids);
      return { deliveriesTableSnapshot };
    },
    onError: (error, _variables, context) => {
      if (context?.deliveriesTableSnapshot) {
        restoreDeliveriesTableQueries(queryClient, context.deliveriesTableSnapshot);
      }
      toastBulkMutationError(error, t);
    },
    onSuccess: async (data, variables) => {
      await invalidateCachesForDeliveryIds(queryClient, variables.input.ids);
      toastBulkDeliveryResult(
        t,
        data.bulkCancelDeliveries,
        "network.delivery.bulk.cancelSuccess",
        "network.delivery.bulk.partialOutcome"
      );
      table.resetRowSelection();
      setDialogOpen(false);
      setReason("");
      setAdditionalInfo("");
    },
  });

  if (!visible) {
    return null;
  }

  const handleCloseDialog = () => {
    if (isPending) {
      return;
    }
    setDialogOpen(false);
    setReason("");
    setAdditionalInfo("");
  };

  const handleSubmit = () => {
    if (!reason) {
      toast.error(t("common.form.errors.required"), { position: "bottom-right" });
      return;
    }
    if (reason === "other" && !additionalInfo.trim()) {
      toast.error(t("common.form.errors.required"), { position: "bottom-right" });
      return;
    }
    void mutateAsync({
      input: {
        ids: selectedData.map((r) => r.id),
        reason: {
          reason,
          additionalInfo: additionalInfo.trim() || undefined,
        },
      },
    });
  };

  return (
    <>
      <Tooltip title={t("delivery.action.cancel.title")}>
        <span>
          <Button
            color="warning"
            disabled={selectedData.length === 0 || isPending}
            onClick={() => setDialogOpen(true)}
            size="small"
            startIcon={<CancelIcon fontSize="small" />}
            variant="contained"
          >
            {t("delivery.action.cancel.text")}
          </Button>
        </span>
      </Tooltip>

      <Dialog fullWidth maxWidth="sm" onClose={handleCloseDialog} open={dialogOpen}>
        <DialogTitle>
          {t("delivery.action.cancel.title")} ({selectedData.length})
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Alert severity="warning">
            {t("delivery.table.bulkConfirm.cancel.summary", { count: selectedData.length })}
          </Alert>
          <FormControl required size="small" sx={{ mt: 1 }}>
            <InputLabel id="bulk-cancel-reason-label">{t("common.shared.reason")}</InputLabel>
            <Select
              label={t("common.shared.reason")}
              labelId="bulk-cancel-reason-label"
              onChange={(e: SelectChangeEvent<(typeof cancellationReasonKeys)[number]>) => {
                setReason(e.target.value as (typeof cancellationReasonKeys)[number]);
              }}
              value={reason}
            >
              {cancellationReasonKeys.map((key) => (
                <MenuItem key={key} value={key}>
                  {t(`delivery.cancelReason.${key}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t("common.shared.additional-info")}
            minRows={2}
            multiline
            onChange={(e) => setAdditionalInfo(e.target.value)}
            size="small"
            value={additionalInfo}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={isPending} onClick={handleCloseDialog}>
            {t("common.actions.cancel")}
          </Button>
          <Button color="warning" disabled={isPending} onClick={handleSubmit} variant="contained">
            {t("delivery.action.cancel.text")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
